import React, { useState } from "react";

import {
	Badge,
	Button,
	ConfirmModal,
	InfoMessage,
	Input,
	Modal,
	PasswordField,
	SectionCard,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import PasskeysService, { isCancelled } from "@/services/PasskeysService";
import UserService from "@/services/UserService";

import { meKeys } from "@/constants/CQueryKeys";

import { TPasskey } from "@/types";

import { errorToast, promiseToast } from "@/modules/index";
import { cn } from "@/utils";

interface IPasskeysCardProps {
	/** The account still has a password: it can go once a passkey exists. */
	hasPassword: boolean;
	className?: string;
}

/** A name the list can tell apart: the browser and the system, when they say. */
const suggestedName = (): string => {
	const ua = navigator.userAgent;
	const system = /Mac/.test(ua)
		? "Mac"
		: /iPhone|iPad/.test(ua)
			? "iPhone"
			: /Android/.test(ua)
				? "Android"
				: /Windows/.test(ua)
					? "Windows"
					: "";
	return system ? `Passkey ${system}` : "Passkey";
};

/**
 * The profile's passkeys: add one (the device asks its owner), rename,
 * remove - and, once one exists, sign in without a password at all.
 * Hidden on a browser without WebAuthn.
 */
export const PasskeysCard: React.FC<IPasskeysCardProps> = ({
	hasPassword,
	className,
}) => {
	const queryClient = useQueryClient();
	const [naming, setNaming] = useState<TPasskey | "new" | null>(null);
	const [name, setName] = useState("");
	const [deleting, setDeleting] = useState<TPasskey | null>(null);
	const [removingPassword, setRemovingPassword] = useState(false);
	const [currentPassword, setCurrentPassword] = useState("");

	const { data: passkeys = [] } = useQuery({
		queryKey: meKeys.passkeys,
		queryFn: PasskeysService.list,
	});
	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: meKeys.all });

	const save = useMutation({
		mutationFn: () =>
			naming === "new"
				? PasskeysService.add(name)
				: PasskeysService.rename((naming as TPasskey).id, name),
		onSuccess: () => {
			setNaming(null);
			void refresh();
		},
	});
	const remove = useMutation({
		mutationFn: (id: number) => PasskeysService.remove(id),
		onSuccess: () => void refresh(),
	});
	const dropPassword = useMutation({
		mutationFn: () => UserService.removePassword(currentPassword),
		onSuccess: () => {
			setRemovingPassword(false);
			setCurrentPassword("");
			void refresh();
		},
	});

	if (!PasskeysService.isSupported()) return null;

	return (
		<SectionCard
			title={t("account.passkeys.title")}
			className={cn([className])}
			actions={
				<Button
					size="sm"
					picto="add"
					onClick={() => {
						setName(suggestedName());
						setNaming("new");
					}}
					data-testid="passkey-add"
				>
					{t("account.passkeys.add")}
				</Button>
			}
		>
			<div className="flex flex-col gap-3">
				<p className="text-sm text-neutral-500">
					{t("account.passkeys.intro")}
				</p>
				{passkeys.length > 0 && (
					<ul className="flex flex-col divide-y divide-neutral-100">
						{passkeys.map((passkey) => (
							<li
								key={passkey.id}
								className="flex flex-wrap items-center justify-between gap-2 py-2"
								data-testid={`passkey-${passkey.id}`}
							>
								<span className="flex flex-col">
									<span className="flex items-center gap-2 font-medium">
										{passkey.name}
										{passkey.backedUp && (
											<Badge
												size="sm"
												variant="tint"
												color="info"
											>
												{t("account.passkeys.synced")}
											</Badge>
										)}
									</span>
									<span className="text-xs text-neutral-500">
										{t("account.passkeys.dates", {
											created: format(
												new Date(passkey.createdAt),
												"dd/MM/yyyy"
											),
											used: passkey.lastUsedAt
												? format(
														new Date(
															passkey.lastUsedAt
														),
														"dd/MM/yyyy"
													)
												: "—",
										})}
									</span>
								</span>
								<span className="flex gap-1">
									<Button
										size="sm"
										variant="ghost"
										color="neutral"
										picto="pen"
										onClick={() => {
											setName(passkey.name);
											setNaming(passkey);
										}}
									>
										{t("actions.edit")}
									</Button>
									<Button
										size="sm"
										variant="ghost"
										color="danger"
										picto="trash"
										onClick={() => setDeleting(passkey)}
									>
										{t("actions.delete")}
									</Button>
								</span>
							</li>
						))}
					</ul>
				)}
				{hasPassword && passkeys.length > 0 && (
					<div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-neutral-50 p-3">
						<span className="text-sm">
							{t("account.passkeys.passwordless")}
						</span>
						<Button
							size="sm"
							variant="outline"
							onClick={() => setRemovingPassword(true)}
							data-testid="password-remove"
						>
							{t("account.passkeys.removePassword")}
						</Button>
					</div>
				)}
			</div>

			<Modal
				open={naming !== null}
				onClose={() => setNaming(null)}
				title={
					naming === "new"
						? t("account.passkeys.add")
						: t("account.passkeys.rename")
				}
				portal
				footer={
					<Button
						isLoading={save.isPending}
						disabled={!name.trim()}
						onClick={() =>
							void errorToast(
								save.mutateAsync().catch((error: unknown) => {
									if (!isCancelled(error)) throw error;
								})
							)
						}
						data-testid="passkey-save"
					>
						{naming === "new"
							? t("account.passkeys.create")
							: t("actions.save")}
					</Button>
				}
			>
				<div className="flex flex-col gap-3">
					<Input
						label={t("account.passkeys.name")}
						value={name}
						onChange={setName}
						maxLength={64}
					/>
					{naming === "new" && (
						<p className="text-sm text-neutral-500">
							{t("account.passkeys.deviceWillAsk")}
						</p>
					)}
				</div>
			</Modal>

			<ConfirmModal
				open={deleting !== null}
				onClose={() => setDeleting(null)}
				onConfirm={() => {
					if (deleting) promiseToast(remove.mutateAsync(deleting.id));
					setDeleting(null);
				}}
				title={t("account.passkeys.deleteTitle")}
				confirmText={t("actions.delete")}
				confirmColor="danger"
				cancelText={t("actions.cancel")}
				portal
			>
				{t("account.passkeys.deleteText", { name: deleting?.name })}
			</ConfirmModal>

			<Modal
				open={removingPassword}
				onClose={() => setRemovingPassword(false)}
				title={t("account.passkeys.removePassword")}
				portal
				footer={
					<Button
						color="danger"
						isLoading={dropPassword.isPending}
						disabled={!currentPassword}
						onClick={() => promiseToast(dropPassword.mutateAsync())}
						data-testid="password-remove-confirm"
					>
						{t("account.passkeys.removePassword")}
					</Button>
				}
			>
				<div className="flex flex-col gap-3">
					<InfoMessage color="info">
						{t("account.passkeys.removePasswordText")}
					</InfoMessage>
					<PasswordField
						label={t("forms.password.current")}
						name="currentPassword"
						autoComplete="current-password"
						value={currentPassword}
						onChange={setCurrentPassword}
					/>
				</div>
			</Modal>
		</SectionCard>
	);
};
